import {
  deleteFcmToken,
  getFcmToken,
  getMessagingInstance,
  onForegroundMessage,
  requestPushPermission,
} from "./firebase";
import { getReadyServiceWorkerRegistration } from "./pwaRegistration";
import { isAuthenticated } from "src/Funcs/auth/authStore";
import {
  deleteUserFcmToken,
  upsertUserFcmToken,
} from "./pushNotificationsBackend";

export async function isPushSupported() {
  if (typeof window === "undefined") return false;
  if (!("Notification" in window)) return false;
  if (!("serviceWorker" in navigator)) return false;
  const msg = await getMessagingInstance();
  return Boolean(msg);
}

/**
 * Enable push notifications and return the FCM token.
 *
 * Typical usage:
 * const { token } = await enablePushNotifications({
 *   onToken: (t) => fetch("/api/save-fcm-token", { ... }),
 *   onMessage: (payload) => console.log("FG message", payload),
 * })
 */
export async function enablePushNotifications({ onToken, onMessage } = {}) {
  if (typeof window === "undefined")
    return { token: null, unsubscribe: () => {} };

  const supported = await isPushSupported();
  if (!supported) return { token: null, unsubscribe: () => {} };

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await requestPushPermission();

  console.log({ permission });

  if (permission !== "granted") return { token: null, unsubscribe: () => {} };

  // Use the already-registered PWA service worker (from `registerSW` in `main.jsx`).
  const registration = await getReadyServiceWorkerRegistration({
    timeoutMs: 10000,
  });
  if (!registration) {
    console.warn(
      "[Doitify] Service Worker not ready, push notifications unavailable.",
    );
    return { token: null, unsubscribe: () => {} };
  }
  console.log({ registration });
  const token = await getFcmToken({ registration });

  console.log({ token });

  // Default behavior: store token locally and upsert to backend (if logged in).
  if (token) {
    localStorage.setItem("fcmToken", token);
    try {
      if (isAuthenticated()) {
        await upsertUserFcmToken(token);
        localStorage.setItem("fcmTokenLastSynced", token);
      }
    } catch (e) {
      // Don't break UX if backend isn't ready yet.
      console.warn("FCM token backend upsert failed", e);
    }
  }

  if (token && typeof onToken === "function") {
    await onToken(token);
  }

  const unsubscribe =
    typeof onMessage === "function"
      ? await onForegroundMessage(onMessage)
      : () => {};

  return { token, unsubscribe };
}

/**
 * Disable push for this browser by deleting the current FCM token.
 * Note: backend should also remove the token server-side (optional via `onTokenDeleted`).
 */
export async function disablePushNotifications({ onTokenDeleted } = {}) {
  const token = localStorage.getItem("fcmToken");
  const deleted = await deleteFcmToken();
  localStorage.removeItem("fcmToken");
  localStorage.removeItem("fcmTokenLastSynced");
  try {
    if (isAuthenticated()) {
      await deleteUserFcmToken(token || undefined);
    }
  } catch (e) {
    console.warn("FCM token backend delete failed", e);
  }
  if (deleted && typeof onTokenDeleted === "function") {
    await onTokenDeleted();
  }
  return { deleted };
}

/**
 * Sync token to backend if permission is already granted.
 * Does NOT request permission (safe to call on app load / login).
 */
export async function syncFcmTokenToBackendIfGranted() {
  if (typeof window === "undefined") return { synced: false, token: null };
  if (Notification.permission !== "granted")
    return { synced: false, token: null };
  if (!isAuthenticated()) return { synced: false, token: null };

  const registration = await getReadyServiceWorkerRegistration({
    timeoutMs: 10000,
  });
  if (!registration) return { synced: false, token: null };
  const token = await getFcmToken({ registration });
  if (!token) return { synced: false, token: null };

  const lastSynced = localStorage.getItem("fcmTokenLastSynced");
  if (lastSynced === token) return { synced: false, token };

  await upsertUserFcmToken(token);
  localStorage.setItem("fcmToken", token);
  localStorage.setItem("fcmTokenLastSynced", token);
  return { synced: true, token };
}
